import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../../utils/supabase';

const WEBSITE_URL = 'https://qiuyating.com';
const FOOTER_QR_CODE_SRC = '/qrcode_for_gh_a4ef89c983c4_258.webp';
const DEFAULT_WEBSITE_LAUNCH_DATE = '2025-09-25';
const FOOTER_HEADING_CLASS = 'mb-3 text-2xl font-normal leading-tight tracking-normal text-gray-800 md:text-[2rem]';
const FOOTER_TEXT_CLASS = 'text-base leading-6 tracking-normal md:text-[17px] md:leading-7 [&>p]:m-0';
const FOOTER_IMAGE_CLASS = 'h-28 w-28';

export const DEFAULT_FOOTER_SETTINGS = {
  copyrightTitle: 'Copyright ©',
  copyrightText: 'Copyright © 2026 Yating Qiu. All rights reserved.',
  description:
    'This personal academic website presents my education, service, programs, honors, and publications.',
  lastUpdated: '2026.07.18',
  launchDate: DEFAULT_WEBSITE_LAUNCH_DATE,
  noticeTitle: 'Advance Notice',
  noticeText: 'New updates about publications, programs, and academic activities will be added here.',
  noticeSubtext: 'Thank you for visiting — please feel free to contact me for academic communication.',
  contactTitle: 'Contact & Visit Me',
  email: 'qiuyating.888@163.com',
  githubLabel: 'yatingq',
  githubUrl: 'https://github.com/yatingq',
  homepageLabel: 'qiuyating.com',
  homepageUrl: WEBSITE_URL,
  popularTitle: 'Popular & Important',
  popularText: 'Scan the QR code to visit and save my homepage.',
  badgeText: 'YQ',
  showBadge: true,
};

export type FooterSettings = typeof DEFAULT_FOOTER_SETTINGS;

function formatShanghaiTime(date: Date) {
  return new Intl.DateTimeFormat('zh-CN', {
    timeZone: 'Asia/Shanghai',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
    .format(date)
    .replace(/\//g, '.');
}

function parseLaunchDate(value: string) {
  const rawDate = value?.trim() || DEFAULT_WEBSITE_LAUNCH_DATE;
  const normalizedDate = /^\d{4}-\d{2}-\d{2}$/.test(rawDate) ? `${rawDate}T00:00:00+08:00` : rawDate;
  const launchDate = new Date(normalizedDate);

  if (Number.isNaN(launchDate.getTime())) {
    return new Date(`${DEFAULT_WEBSITE_LAUNCH_DATE}T00:00:00+08:00`);
  }

  return launchDate;
}

function formatRunDuration(now: Date, launchDateValue: string) {
  const launchDate = parseLaunchDate(launchDateValue);
  const diffSeconds = Math.max(0, Math.floor((now.getTime() - launchDate.getTime()) / 1000));
  const days = Math.floor(diffSeconds / 86400);
  const hours = Math.floor((diffSeconds % 86400) / 3600);
  const minutes = Math.floor((diffSeconds % 3600) / 60);
  const seconds = diffSeconds % 60;

  return `${days} days ${hours}h ${minutes}m ${seconds}s`;
}

export default function SiteFooter() {
  const location = useLocation();
  const [now, setNow] = useState(() => new Date());
  const [settings, setSettings] = useState<FooterSettings>(DEFAULT_FOOTER_SETTINGS);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const loadFooterSettings = async () => {
      if (!supabase) {
        return;
      }

      const { data, error } = await supabase
        .from('footer_settings')
        .select('settings')
        .eq('id', 1)
        .maybeSingle();

      if (error) {
        console.warn('读取页脚设置失败，使用默认配置:', error.message);
        return;
      }

      if (data?.settings && typeof data.settings === 'object') {
        setSettings({ ...DEFAULT_FOOTER_SETTINGS, ...(data.settings as Partial<FooterSettings>) });
      }
    };

    void loadFooterSettings();
  }, []);

  if (location.pathname.startsWith('/admin')) {
    return null;
  }

  return (
    <footer className="mt-16 border-t border-gray-200 bg-gray-100/95 text-gray-700 shadow-inner">
      <div className="mx-auto grid max-w-6xl gap-x-16 gap-y-10 px-6 py-10 md:grid-cols-2 lg:px-8">
        <div className="space-y-8">
          <section>
            <h2 className={FOOTER_HEADING_CLASS}>{settings.copyrightTitle}</h2>
            <div className={FOOTER_TEXT_CLASS}>
              <p>{settings.copyrightText}</p>
              <p>{settings.description}</p>
              <p>Last updated on {settings.lastUpdated}.</p>
              <p>Current Time: {formatShanghaiTime(now)}</p>
              <p>The Website has run: {formatRunDuration(now, settings.launchDate)}</p>
            </div>
          </section>

          <section>
            <h2 className={FOOTER_HEADING_CLASS}>{settings.noticeTitle}</h2>
            <div className={FOOTER_TEXT_CLASS}>
              <p>
                <span className="font-semibold text-red-500">{settings.noticeText}</span>
              </p>
              <p>{settings.noticeSubtext}</p>
            </div>
          </section>
        </div>

        <div className="space-y-8">
          <section>
            <h2 className={FOOTER_HEADING_CLASS}>{settings.contactTitle}</h2>
            <div className={FOOTER_TEXT_CLASS}>
              <p>
                E-mail:{' '}
                <a className="text-sky-600 hover:text-sky-700 hover:underline" href={`mailto:${settings.email}`}>
                  {settings.email}
                </a>
              </p>
              <p>
                Github:{' '}
                <a
                  className="text-sky-600 hover:text-sky-700 hover:underline"
                  href={settings.githubUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {settings.githubLabel}
                </a>
              </p>
              <p>
                Homepage:{' '}
                <a
                  className="text-sky-600 hover:text-sky-700 hover:underline"
                  href={settings.homepageUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {settings.homepageLabel}
                </a>
              </p>
            </div>
          </section>

          <section>
            <h2 className={FOOTER_HEADING_CLASS}>{settings.popularTitle}</h2>
            <p className="mb-5 text-base leading-6 tracking-normal md:text-[17px] md:leading-7">{settings.popularText}</p>
            <div className="flex flex-wrap items-center gap-4">
              <img
                src={FOOTER_QR_CODE_SRC}
                alt={`QR code for ${settings.homepageLabel}`}
                loading="lazy"
                decoding="async"
                className={`${FOOTER_IMAGE_CLASS} rounded bg-white p-2 shadow-sm`}
              />
              {settings.showBadge && (
                <div className={`${FOOTER_IMAGE_CLASS} flex items-center justify-center rounded-full bg-gradient-to-br from-sky-400 via-blue-500 to-indigo-700 text-3xl font-semibold text-white shadow-sm`}>
                  {settings.badgeText}
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </footer>
  );
}
